import React, { useState, FC } from "react";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { navigate } from "@reach/router";
import { CSVLink } from "react-csv";

import { useStyles } from "./styles";
import { ReactComponent as MoreOptionsIcon } from "static/media/moreOptionsIcon.svg";
import { ReactComponent as ViewIcon } from "static/media/viewIconBlack.svg";
import { ReactComponent as EditIcon } from "static/media/editIcon.svg";
import { ReactComponent as PauseIcon } from "static/media/pauseIconBlack.svg";
import { ReactComponent as ArchiveIcon } from "static/media/saveIcon.svg";
import { ReactComponent as ExportIcon } from "static/media/downloadIcon.svg";
import { firestore } from "firebase-tools/firebase";
import { customHeaders } from "../../../constants";
import { SocialMediaPlatform } from "containers/BeAmplified/Campaigns/Organic/SelectCampaign";
import { csvHeaders } from "../../View/Extractor";

const MoreOptions: FC<{
  campaign: any;
  platform: SocialMediaPlatform;
}> = ({ campaign, platform }) => {
  const classes = useStyles({});
  const [open, setOpen] = useState(false);

  const typesMap = new Map();
  // IG Maps
  typesMap.set(
    `${platform}-commentsExtractor-scrapper`,
    "instagram-comments-extractor"
  );
  typesMap.set(
    `${platform}-hashtag-scrapper`,
    "instagram-autofollow-campaigns"
  );
  typesMap.set(`${platform}-autoFollow`, "instagram-autofollow-campaigns");
  typesMap.set(`${platform}-igAutoCommenter`, "instagram-autocommenter");
  typesMap.set(`${platform}-igAutoLiker`, "instagram-auto-liker");

  // TWITTER Maps
  typesMap.set(`${platform}-autofollow`, "twitter-campaigns");
  typesMap.set(`${platform}-search-scrapper`, "twitter-campaigns");
  typesMap.set(`${platform}-autoPoster`, "twitter-auto-poster");
  typesMap.set(`twitter-auto-dm`, "twitter-auto-dm");
  typesMap.set(`${platform}-autoLiker`, "twitter-auto-liker");

  // FB Maps
  typesMap.set(`${platform}-groupScrapper-scrapper`, "facebook-group-scrapper");
  typesMap.set(`${platform}-groupScrapper`, "facebook-group-scrapper");
  typesMap.set(`${platform}-fbAutoLiker`, "facebook-auto-liker");
  typesMap.set(`facebook-auto-dm`, "facebook-auto-dm");

  const toggleStatus = (status: string): string => {
    let newStatus = "";
    switch (status) {
      case "running":
        newStatus = "paused";
        break;
      case "paused":
        newStatus = "running";
        break;
      default:
        newStatus = "paused";
    }
    return newStatus;
  };

  const togglePause = async (type: string) => {
    const history = campaign.history || [];
    history.push({
      status: campaign.status,
      date: new Date(),
    });
    await firestore
      .collection(typesMap.get(`${platform}-${type}`))
      .doc(campaign.id)
      .update({
        status: toggleStatus(campaign.status),
        history,
      });
  };

  const archiveCampaign = async (type: string) => {
    await firestore
      .collection(typesMap.get(`${platform}-${type}`))
      .doc(campaign.id)
      .update({
        archived: true,
      });
  };

  return (
    <div style={{ position: "relative" }}>
      <MoreOptionsIcon
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      />

      {open ? (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div className={classes.container}>
            <div
              onClick={() => {
                if (campaign.campaignType === "autoPoster") {
                  navigate(`/campaigns/organic/autoposter/${campaign.id}`);
                  return;
                } else if (campaign.campaignType === "auto-dm") {
                  if (
                    platform === SocialMediaPlatform.Facebook ||
                    platform === SocialMediaPlatform.Twitter
                  ) {
                    navigate(
                      `/campaigns/organic/${platform}/auto-dm/${campaign.id}`
                    );
                    return;
                  }
                  navigate(`/campaigns/organic/auto-dm/${campaign.id}`);
                  return;
                } else if (campaign.campaignType === "autoLiker") {
                  navigate(
                    `/campaigns/organic/auto-liker/${SocialMediaPlatform.Twitter.toLowerCase()}/${
                      campaign.id
                    }`
                  );
                  return;
                } else if (campaign.campaignType === "igAutoLiker") {
                  navigate(
                    `/campaigns/organic/auto-liker/${SocialMediaPlatform.Instagram.toLowerCase()}/${
                      campaign.id
                    }`
                  );
                  return;
                } else if (campaign.campaignType === "fbAutoLiker") {
                  navigate(
                    `/campaigns/organic/auto-liker/${SocialMediaPlatform.Facebook.toLowerCase()}/${
                      campaign.id
                    }`
                  );
                  return;
                } else if (campaign.campaignType === "igAutoCommenter") {
                  navigate(
                    `/campaigns/organic/auto-commenter/${SocialMediaPlatform.Instagram.toLowerCase()}/${
                      campaign.id
                    }`
                  );
                  return;
                } else if (
                  campaign.campaignType.toLowerCase() === "autofollow"
                ) {
                  navigate(`/campaigns/organic/${platform}/${campaign.id}`);
                  return;
                } else if (campaign.campaignType === "groupScrapper") {
                  navigate(
                    `/campaigns/organic/extractor/${SocialMediaPlatform.Facebook.toLowerCase()}/${
                      campaign.id
                    }`
                  );
                  return;
                } else if (
                  campaign.campaignType === "commentsExtractor-scrapper"
                ) {
                  navigate(
                    `/campaigns/organic/extractor/${SocialMediaPlatform.Instagram.toLowerCase()}/${
                      campaign.id
                    }`
                  );
                  return;
                }

                navigate(`/campaigns/organic/${platform}/${campaign.id}`);
              }}
              className={classes.item}
            >
              View
              <ViewIcon />
            </div>
            {["autofollow", "auto-dm"].includes(
              campaign.campaignType.toLowerCase()
            ) && (
              <div
                onClick={() =>
                  navigate(
                    `/campaigns/organic/${
                      campaign.campaignType === "auto-dm"
                        ? `create/${platform}/auto-dm/`
                        : `${platform}/`
                    }${campaign.id}/edit`
                  )
                }
                className={classes.item}
              >
                Edit
                <EditIcon />
              </div>
            )}
            {!["groupScrapper"].includes(campaign?.campaignType) && (
              <div
                onClick={() => togglePause(campaign.campaignType)}
                className={classes.item}
              >
                {campaign.status === "paused" ? "Resume" : "Pause"}
                <PauseIcon />
              </div>
            )}
            <div
              onClick={() => archiveCampaign(campaign.campaignType)}
              className={classes.item}
            >
              Archive
              <ArchiveIcon />
            </div>
            {campaign?.resultObject &&
              campaign?.resultObject !== "[]" &&
              !campaign?.resultObject?.csvURL && (
                <CSVLink
                  data={
                    campaign?.resultObject?.length
                      ? campaign?.resultObject || []
                      : JSON.parse(campaign?.resultObject || "[]")
                  }
                  headers={
                    campaign?.campaignType === "groupScrapper"
                      ? csvHeaders
                      : customHeaders
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  filename="results.csv"
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <div className={classes.item}>
                    Export
                    <ExportIcon />
                  </div>
                </CSVLink>
              )}
            {campaign?.resultObject && campaign?.resultObject?.csvURL && (
              <a
                href={`${campaign?.resultObject?.csvURL}`}
                className={classes.item}
              >
                Export
                <ExportIcon />
              </a>
            )}
          </div>
        </ClickAwayListener>
      ) : null}
    </div>
  );
};

export default MoreOptions;
